import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useCurrentStudentProfile,
  useStudentJourneyData,
} from '../../../hooks/useLmsData';
import { useStudentWorkspace } from '../../../contexts/LmsDataContext';
import { syncStudentAchievements } from '../../../utils/lmsMutations';
import {
  StudentActionLink,
  StudentBadge,
  StudentCard,
  StudentEmpty,
  StudentPage,
  StudentProgressBar,
  StudentSection,
} from '../../student/StudentUI';
import {
  buildStudentProfileSummary,
  buildStudentTaskList,
  formatStudentCurrency,
  formatStudentDate,
  getNextStudentLesson,
  getStudentCourseAction,
  getStudentDashboardState,
} from '../../../utils/studentExperience';
import { getSuspensionLabel, suspensionMessage } from '../../../utils/accessSuspensions';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { data: profileData, loading: profileLoading } =
    useCurrentStudentProfile(currentUser?.uid);
  const { data: courseData, loading: courseLoading } = useStudentWorkspace();
  const { loading: journeyLoading } = useStudentJourneyData({
    user: currentUser,
    student: profileData?.student,
    courseData,
    ready: !courseLoading,
  });

  useEffect(() => {
    if (!currentUser || !profileData?.student || courseLoading || journeyLoading) {
      return;
    }

    syncStudentAchievements({
      user: currentUser,
      student: profileData.student,
      courseData,
    }).catch((syncError) => {
      console.error('Error syncing achievements:', syncError);
    });
  }, [courseData, courseLoading, currentUser, journeyLoading, profileData]);

  const activeCourses = useMemo(() => courseData?.activeCourses || [], [courseData]);
  const allCourses = useMemo(() => dataToCourseCards(courseData), [courseData]);
  const paymentRequests = useMemo(
    () => courseData?.paymentRequests || [],
    [courseData],
  );
  const payments = useMemo(() => courseData?.payments || [], [courseData]);
  const profileSummary = useMemo(
    () => buildStudentProfileSummary(currentUser, profileData?.student),
    [currentUser, profileData],
  );
  const nextLesson = useMemo(() => getNextStudentLesson(activeCourses), [activeCourses]);
  const dashboardState = useMemo(
    () =>
      getStudentDashboardState({
        activeCourses,
        paymentRequests,
        payments,
        profileSummary,
      }),
    [activeCourses, paymentRequests, payments, profileSummary],
  );
  const taskList = useMemo(
    () =>
      buildStudentTaskList({
        state: dashboardState,
        profileSummary,
        paymentRequests,
        payments,
        activeCourses,
        nextLesson,
      }),
    [dashboardState, profileSummary, paymentRequests, payments, activeCourses, nextLesson],
  );

  if (profileLoading || courseLoading || journeyLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-stone-200 border-t-[#d61f74]" />
      </div>
    );
  }

  const isNewStudent = !activeCourses.length;
  const openPaymentRequest = paymentRequests.find((item) => item.status === 'pending');
  const reviewPayment = payments.find((item) => item.status === 'pending');
  const completedPayments = payments.filter((item) => item.status === 'completed');
  const shouldShowPayments = Boolean(
    openPaymentRequest || reviewPayment || completedPayments.length,
  );
  const suspendedCourses = activeCourses.filter((course) => course.courseSuspension);

  return (
    <StudentPage
      title={`Welcome, ${currentUser?.firstName || 'Student'}`}
      subtitle={isNewStudent ? 'Choose a course to begin.' : 'Open your course and follow the next class.'}
    >
      <StudentSection title="Do next">
        {taskList.length ? (
          <TaskBoard tasks={taskList.slice(0, 4)} />
        ) : (
          <StudentEmpty title="No pending task" message="Everything important is already done." />
        )}
      </StudentSection>

      {profileSummary.completion < 100 ? (
        <StudentSection
          title="Profile"
          action={<StudentActionLink to="/profile" variant="secondary">Open profile</StudentActionLink>}
        >
          <StudentCard className="bg-white">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-stone-900">
                    {profileSummary.completedCount}/{profileSummary.totalCount} items done
                  </p>
                  <p className="mt-1 text-sm text-stone-600">
                    Finish the missing details so your account is fully ready.
                  </p>
                </div>
                <span className="text-sm font-semibold text-stone-700">
                  {profileSummary.completion}%
                </span>
              </div>
              <StudentProgressBar value={profileSummary.completion} />
            </div>
          </StudentCard>
        </StudentSection>
      ) : null}

      {isNewStudent ? (
        <>
          <StudentSection
            title="Available courses"
            action={<StudentActionLink to="/courses/available" variant="secondary">View all courses</StudentActionLink>}
          >
            {!allCourses.length ? (
              <StudentEmpty
                title="No course available"
                message="Courses will appear here when the next intake is open."
              />
            ) : (
              <div className="grid gap-4 xl:grid-cols-3">
                {allCourses
                  .filter((course) => course.status !== 'active')
                  .slice(0, 6)
                  .map((course) => (
                    <CoursePreviewCard
                      key={course.id}
                      course={course}
                      onOpen={() => navigate(`/courses/${course.id}`)}
                    />
                  ))}
              </div>
            )}
          </StudentSection>

          {shouldShowPayments ? (
            <StudentSection title="Payments">
              <PaymentDashboardCard
                openPaymentRequest={openPaymentRequest}
                reviewPayment={reviewPayment}
                completedPayments={completedPayments}
              />
            </StudentSection>
          ) : null}
        </>
      ) : (
        <>
          {suspendedCourses.length ? (
            <StudentSection title="Access notices">
              <div className="grid gap-3 xl:grid-cols-2">
                {suspendedCourses.map((course) => (
                  <StudentCard key={course.id} className="border-amber-200 bg-amber-50">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-amber-950">
                          {getSuspensionLabel(course.courseSuspension.level)}
                        </p>
                        <p className="mt-1 text-sm text-amber-900">
                          {suspensionMessage(course.courseSuspension, course.name)}
                        </p>
                      </div>
                      <StudentActionLink
                        to={`/courses/my-courses/${course.id}`}
                        variant="secondary"
                      >
                        View course
                      </StudentActionLink>
                    </div>
                  </StudentCard>
                ))}
              </div>
            </StudentSection>
          ) : null}

          <StudentSection
            title="My courses"
            action={<StudentActionLink to="/courses/my-courses" variant="secondary">All courses</StudentActionLink>}
          >
            <div className="grid gap-4 xl:grid-cols-3">
              {activeCourses.slice(0, 6).map((course) => (
                <ActiveCourseCard
                  key={course.id}
                  course={course}
                  onOpen={() => navigate(`/courses/my-courses/${course.id}`)}
                />
              ))}
            </div>
          </StudentSection>

          <div className="grid gap-5 xl:grid-cols-2">
            <StudentSection title="Next class">
              {nextLesson ? (
                <StudentCard className="bg-white">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-lg font-semibold text-stone-900">{nextLesson.title}</p>
                      <p className="mt-1 text-sm text-stone-600">
                        {nextLesson.courseName} on {formatStudentDate(nextLesson.date)}
                      </p>
                    </div>
                    <StudentActionLink to={`/courses/my-courses/${nextLesson.courseId}`}>
                      Open course
                    </StudentActionLink>
                  </div>
                </StudentCard>
              ) : (
                <StudentEmpty
                  title="No class scheduled"
                  message="The next class will appear here when available."
                />
              )}
            </StudentSection>

            <StudentSection title="Payments">
              {shouldShowPayments ? (
                <PaymentDashboardCard
                  openPaymentRequest={openPaymentRequest}
                  reviewPayment={reviewPayment}
                  completedPayments={completedPayments}
                />
              ) : (
                <StudentEmpty
                  title="No payment pending"
                  message="You do not have any payment task right now."
                />
              )}
            </StudentSection>
          </div>
        </>
      )}
    </StudentPage>
  );
};

const PaymentDashboardCard = ({
  openPaymentRequest,
  reviewPayment,
  completedPayments = [],
}) => {
  const recentCompletedPayments = completedPayments.slice(0, 4);
  return (
    <div className="space-y-3">
      {openPaymentRequest || reviewPayment ? (
        <PaymentReminderCard
          openPaymentRequest={openPaymentRequest}
          reviewPayment={reviewPayment}
        />
      ) : null}
      {recentCompletedPayments.length ? (
        <StudentCard className="bg-white">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-lg font-semibold text-stone-900">Payment records</p>
              <p className="mt-1 text-sm text-stone-600">
                Recent approved payments and receipt references.
              </p>
            </div>
            <StudentActionLink to="/payments" variant="secondary">
              View all
            </StudentActionLink>
          </div>
          <div className="mt-4 divide-y divide-stone-100">
            {recentCompletedPayments.map((payment) => (
              <div
                key={payment.id}
                className="grid gap-2 py-3 text-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold text-stone-900">
                      {payment.courseName || 'Course payment'}
                    </p>
                    <StudentBadge tone="success">Approved</StudentBadge>
                  </div>
                  <p className="mt-1 text-stone-500">
                    {payment.paymentCycleLabel
                      ? `${payment.paymentCycleLabel} · `
                      : ''}
                    {formatStudentDate(payment.paymentDate)}
                  </p>
                  <p className="mt-1 text-stone-600">
                    Receipt/reference: {payment.receiptNumber || payment.receiptId || 'Not added'}
                    {payment.receiptUrl ? (
                      <>
                        {' · '}
                        <a
                          href={payment.receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-[#b91c66] hover:text-[#8f144f]"
                        >
                          Open receipt
                        </a>
                      </>
                    ) : null}
                  </p>
                </div>
                <p className="font-semibold text-stone-950">
                  {formatStudentCurrency(payment.amount)}
                </p>
              </div>
            ))}
          </div>
        </StudentCard>
      ) : null}
    </div>
  );
};

const TaskBoard = ({ tasks }) => (
  <StudentCard className="bg-white p-0 overflow-hidden">
    <div className="divide-y divide-stone-100">
      {tasks.map((task) => (
        <div
          key={task.key}
          className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5"
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-stone-900">{task.title}</p>
            <p className="mt-1 text-sm text-stone-600">{task.body}</p>
          </div>
          <StudentActionLink to={task.to} className="sm:min-w-[9rem]">
            {task.action}
          </StudentActionLink>
        </div>
      ))}
    </div>
  </StudentCard>
);

const PaymentReminderCard = ({ openPaymentRequest, reviewPayment }) => {
  if (openPaymentRequest) {
    return (
      <StudentCard className="bg-white">
        <div className="space-y-3">
          <p className="text-lg font-semibold text-stone-900">
            {openPaymentRequest.courseName || 'Registration fee'}
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <Fact
              label="Amount"
              value={formatStudentCurrency(openPaymentRequest.amount)}
            />
            <Fact
              label="Due"
              value={formatStudentDate(openPaymentRequest.dueDate)}
            />
            <Fact
              label="Batch"
              value={openPaymentRequest.batchName || '-'}
            />
          </div>
          <p className="text-sm text-stone-600">
            You can send the receipt or reference number from the Payments page.
          </p>
          <div className="pt-1">
            <StudentActionLink to="/payments">Open payments</StudentActionLink>
          </div>
        </div>
      </StudentCard>
    );
  }

  if (reviewPayment) {
    return (
      <StudentCard className="bg-white">
        <div className="space-y-2">
          <p className="text-lg font-semibold text-stone-900">Receipt sent</p>
          <p className="text-sm text-stone-600">Waiting for review.</p>
          <div className="pt-1">
            <StudentActionLink to="/payments" variant="secondary">
              Open payments
            </StudentActionLink>
          </div>
        </div>
      </StudentCard>
    );
  }

  return null;
};

const CoursePreviewCard = ({ course, onOpen }) => {
  const action = getStudentCourseAction(course);
  const imageSrc = course.imageUrl || course.coverImage || '/default-course-image.jpg';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="overflow-hidden rounded-[24px] border border-stone-200 bg-white text-left shadow-sm transition hover:border-stone-300 hover:shadow-md"
    >
      <img
        src={imageSrc}
        alt={course.name}
        className="h-44 w-full object-cover"
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = '/default-course-image.jpg';
        }}
      />
      <div className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="line-clamp-2 text-lg font-semibold text-stone-900">{course.name}</p>
            <p className="mt-1 text-sm text-stone-500">{course.type || 'Course'}</p>
          </div>
          <span className="shrink-0 text-xs font-medium text-stone-400">
            {course.batches?.[0]?.batchId || ''}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm text-stone-600">
          <Fact label="Fee" value={formatStudentCurrency(course.fees?.fullCourseFee)} />
          <Fact
            label="Duration"
            value={
              course.duration?.isOneDay
                ? 'One day'
                : `${course.duration?.months || 0} months`
            }
          />
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-stone-100 pt-3">
          <span className="text-sm font-medium text-stone-700">View course</span>
          <span className="inline-flex min-h-[40px] items-center rounded-2xl bg-[#d61f74] px-4 text-sm font-semibold text-white shadow-[0_16px_30px_-18px_rgba(214,31,116,0.55)]">
            {action.label}
          </span>
        </div>
      </div>
    </button>
  );
};

const ActiveCourseCard = ({ course, onOpen }) => {
  const imageSrc = course.imageUrl || course.coverImage || '/default-course-image.jpg';
  const suspension = course.courseSuspension;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="overflow-hidden rounded-[24px] border border-stone-200 bg-white text-left shadow-sm transition hover:border-stone-300 hover:shadow-md"
    >
      <img
        src={imageSrc}
        alt={course.name}
        className="h-40 w-full object-cover"
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = '/default-course-image.jpg';
        }}
      />
      <div className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="line-clamp-2 text-lg font-semibold text-stone-900">{course.name}</p>
            <p className="mt-1 text-sm text-stone-500">
              {course.totalLessons || 0} lessons
            </p>
          </div>
          <span className="shrink-0 text-sm font-semibold text-stone-600">
            {suspension ? getSuspensionLabel(suspension.level) : `${Number(course.progress || 0)}%`}
          </span>
        </div>
        {suspension ? (
          <p className="rounded-2xl bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
            {suspension.level === 'future_lessons'
              ? 'Past lessons stay available. Future lessons are paused.'
              : 'Course lessons are currently paused.'}
          </p>
        ) : (
          <StudentProgressBar value={Number(course.progress || 0)} />
        )}
        <div className="flex items-center justify-between gap-3 border-t border-stone-100 pt-3">
          <span className="text-sm font-medium text-stone-700">Open course</span>
          <span className="inline-flex min-h-[40px] items-center rounded-2xl bg-[#d61f74] px-4 text-sm font-semibold text-white shadow-[0_16px_30px_-18px_rgba(214,31,116,0.55)]">
            Open course
          </span>
        </div>
      </div>
    </button>
  );
};

const Fact = ({ label, value }) => (
  <div className="rounded-2xl bg-stone-50 px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
      {label}
    </p>
    <p className="mt-1 text-sm font-semibold text-stone-900">{value}</p>
  </div>
);

const dataToCourseCards = (courseData) => {
  const courseMap = courseData?.courseMap || {};
  return (courseData?.allCourses || []).map((course) => ({
    ...course,
    ...(courseMap[course.id] || {}),
  }));
};

export default StudentDashboard;
